'use client'

import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { ClipboardList } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import { Button, DataTableColumnHeader } from '@ui/web/components'
import { formatDate } from '@helper/utils/format'

import { BookGalleryCard } from '../../../components'

type SentCollectionRow = RouterOutputs['myBookCollection']['getAllSentBookCollections']['data'][number]

interface UseColumnsProps {
  type: 'web' | 'school'
  renderLink?: RenderLink
  onOpenRecipients: (sentCollectionId: string) => void
}

export function useColumns({ renderLink, type, onOpenRecipients }: UseColumnsProps): ColumnDef<SentCollectionRow>[] {
  const collectionContent = (row: { original: SentCollectionRow }) => (
    <>
      <span className='srd:text-base srd:font-medium'>{row.original.myBookCollection.name}</span>
      <span className='srd:text-sm'>{row.original.myBookCollection.description}</span>
    </>
  )

  return useMemo(
    () => [
      {
        id: 'collection',
        accessorKey: 'collection',
        enableHiding: false,
        enableSorting: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Pesan' />,
        cell: ({ row }) => (
          <div className='srd:w-fit srd:min-w-[400px] srd:max-w-[400px] srd:items-center srd:flex srd:gap-x-4'>
            <BookGalleryCard
              imageSize={100}
              tabIndex={-1}
              withAnimation={false}
              collection={row.original.myBookCollection.myBookCollectionToBooks}
              className='srd:flex srd:items-center srd:gap-x-4 srd:min-w-[100px] srd:max-w-[100px] srd:min-h-[100px] srd:max-h-[100px]'
              cardClassName='srd:w-fit srd:h-fit srd:min-w-[100px] srd:max-w-[100px] srd:min-h-[100px] srd:max-h-[100px]'
            >
              {renderLink ? (
                renderLink({
                  className:
                    'srd:flex srd:flex-col srd:gap-y-1 srd:w-full srd:min-w-[300px] srd:max-w-[300px] srd:text-left srd:break-all srd:max-h-[100px] srd:overflow-y-auto',
                  children: collectionContent(row),
                  meta: {
                    collectionId: row.original.myBookCollection.id,
                  },
                })
              ) : (
                <div className='srd:flex srd:flex-col srd:gap-y-1 srd:w-full srd:min-w-[300px] srd:max-w-[300px] srd:text-left srd:break-all srd:max-h-[100px] srd:overflow-y-auto'>
                  {collectionContent(row)}
                </div>
              )}
            </BookGalleryCard>
          </div>
        ),
      },
      {
        id: 'book-count',
        accessorKey: 'book-count',
        enableHiding: false,
        enableSorting: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Jumlah Buku' />,
        cell: ({ row }) => (
          <div className='srd:text-nowrap'>{row.original.myBookCollection.myBookCollectionToBooks.length} buku</div>
        ),
      },
      {
        id: 'created-at',
        accessorKey: 'created-at',
        enableHiding: false,
        enableSorting: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Tanggal terkirim' />,
        cell: ({ row }) => (
          <div className='srd:text-nowrap'>
            {formatDate(row.original.createdAt, { dateStyle: 'long', timeStyle: 'short' })}
          </div>
        ),
      },
      {
        id: 'recipients',
        accessorKey: 'recipients',
        enableHiding: false,
        enableSorting: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Penerima' />,
        cell: ({ row }) => (
          <div className='srd:text-nowrap'>
            {row.original.sentBookCollectionToUsers.length} {type === 'web' ? 'anak' : 'murid'}
          </div>
        ),
      },
      {
        id: 'actions',
        enableHiding: false,
        cell: ({ row }) => (
          <Button
            variant='outline'
            className='srd:shrink-0'
            onClick={() => onOpenRecipients(row.original.id)}
            type='button'
          >
            <ClipboardList className='srd:size-4 srd:mr-2 srd:shrink-0' /> Lihat penerima
          </Button>
        ),
      },
    ],
    [type, renderLink, onOpenRecipients],
  )
}
